import { z } from 'zod';

export const idSchema = z.string().min(1).max(80);
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const isoDateTimeSchema = z.string().datetime({ offset: true });

export const customerCreateSchema = z.object({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  phone: z.string().min(6).max(30),
  email: z.string().email().optional(),
  address: z.string().max(500).optional(),
  emergencyContact: z.string().max(300).optional(),
  notes: z.string().max(2000).optional(),
});

export const customerUpdateSchema = customerCreateSchema.partial();

export const customerSchema = customerCreateSchema.extend({
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const dogCreateSchema = z.object({
  customerId: idSchema,
  name: z.string().min(1).max(80),
  breed: z.string().max(120).optional(),
  birthDate: isoDateSchema.optional(),
  weightKg: z.number().positive().max(120).optional(),
  sex: z.enum(['male', 'female', 'unknown']).default('unknown'),
  color: z.string().max(80).optional(),
  photoFileId: idSchema.optional(),
  vaccinationFileIds: z.array(idSchema).default([]),
  medicalConditions: z.string().max(2000).optional(),
  medications: z.string().max(2000).optional(),
  feedingInstructions: z.string().max(2000).optional(),
  behaviorNotes: z.string().max(2000).optional(),
  emergencyNotes: z.string().max(2000).optional(),
  veterinarian: z.string().max(200).optional(),
  microchipNumber: z.string().max(80).optional(),
  sterilized: z.boolean().default(false),
  allergies: z.string().max(2000).optional(),
  behaviorWithDogs: z.string().max(2000).optional(),
  behaviorWithPeople: z.string().max(2000).optional(),
  careSpecifications: z.string().max(2000).optional(),
  ownerComments: z.string().max(2000).optional(),
  careStatus: z.enum(['danger', 'supervised', 'caution', 'sociable', 'shy', 'senior', 'other']).optional(),
  status: z.enum(['active', 'inactive', 'deceased']).default('active'),
});

export const dogUpdateSchema = dogCreateSchema.omit({ customerId: true }).partial();

export const dogSchema = dogCreateSchema.extend({
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const serviceCreateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  category: z.enum(['daycare', 'boarding', 'grooming', 'spa', 'transportation', 'medical', 'treats', 'fee', 'other']),
  active: z.boolean().default(true),
});

export const serviceUpdateSchema = serviceCreateSchema.partial();

export const serviceSchema = serviceCreateSchema.extend({
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const lockedServicePriceSchema = z.object({
  serviceId: idSchema,
  priceId: idSchema,
  amountCents: z.number().int().nonnegative(),
  currency: z.literal('MXN').default('MXN'),
  billingUnit: z.enum(['hour', 'night', 'entrance', 'fixed']),
  effectiveFrom: isoDateSchema,
  effectiveTo: isoDateSchema.optional(),
  lockedAt: isoDateTimeSchema,
});

export const appointmentCreateSchema = z.object({
  customerId: idSchema,
  sourceStayId: idSchema.optional(),
  sourceDaycareId: idSchema.optional(),
  dogIds: z.array(idSchema).min(1),
  serviceCategory: z.enum(['daycare', 'boarding', 'grooming', 'spa', 'transportation', 'medical', 'treats', 'fee', 'other']).default('grooming'),
  serviceIds: z.array(idSchema).min(1),
  servicePriceLocks: z.record(idSchema, lockedServicePriceSchema).optional(),
  scheduledAt: isoDateTimeSchema,
  notes: z.string().max(2000).optional(),
});

export const appointmentUpdateSchema = z.object({
  dogIds: z.array(idSchema).min(1).optional(),
  serviceCategory: z.enum(['daycare', 'boarding', 'grooming', 'spa', 'transportation', 'medical', 'treats', 'fee', 'other']).optional(),
  serviceIds: z.array(idSchema).min(1).optional(),
  servicePriceLocks: z.record(idSchema, lockedServicePriceSchema).optional(),
  scheduledAt: isoDateTimeSchema.optional(),
  notes: z.string().max(2000).optional(),
  status: z.enum(['scheduled', 'done', 'cancelled']).optional(),
  completedAt: isoDateTimeSchema.optional(),
  completedByUserId: idSchema.optional(),
  completedByName: z.string().min(1).max(160).optional(),
});

export const appointmentSchema = appointmentCreateSchema.extend({
  id: idSchema,
  status: z.enum(['scheduled', 'done', 'cancelled']),
  completedAt: isoDateTimeSchema.optional(),
  completedByUserId: idSchema.optional(),
  completedByName: z.string().min(1).max(160).optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const priceCreateSchema = z.object({
  serviceId: idSchema,
  amountCents: z.number().int().nonnegative(),
  currency: z.literal('MXN').default('MXN'),
  billingUnit: z.enum(['hour', 'night', 'entrance', 'fixed']).default('fixed'),
  effectiveFrom: isoDateSchema,
  effectiveTo: isoDateSchema.optional(),
  active: z.boolean().default(true),
});

export const priceSchema = priceCreateSchema.extend({
  id: idSchema,
  createdAt: isoDateTimeSchema,
});

export const daycareCreateSchema = z.object({
  customerId: idSchema,
  dogId: idSchema,
  dogIds: z.array(idSchema).optional(),
  startAt: isoDateTimeSchema,
  endAt: isoDateTimeSchema.optional(),
  serviceIds: z.array(idSchema).default([]),
  dogServiceIds: z.record(idSchema, z.array(idSchema)).optional(),
  servicePriceLocks: z.record(idSchema, lockedServicePriceSchema).optional(),
  membershipPurchaseId: idSchema.optional(),
  prepaidByMembership: z.boolean().default(false),
  billingMode: z.enum(['hourly', 'entrance-package']).default('hourly'),
  entranceLimit: z.number().int().positive().max(500).optional(),
  entrancesUsed: z.number().int().nonnegative().default(0),
  notes: z.string().max(2000).optional(),
});

export const checkInEvidenceSchema = z.object({
  fileId: idSchema,
  objectKey: z.string().min(1).max(600),
  fileName: z.string().min(1).max(180),
  contentType: z.string().min(3).max(120),
  kind: z.enum(['document', 'signature']),
  capturedAt: isoDateTimeSchema,
});

export const checkInRequestSchema = z.object({
  evidence: checkInEvidenceSchema.optional(),
});

export const daycareUpdateSchema = z.object({
  dogIds: z.array(idSchema).optional(),
  startAt: isoDateTimeSchema.optional(),
  endAt: isoDateTimeSchema.optional(),
  serviceIds: z.array(idSchema).optional(),
  dogServiceIds: z.record(idSchema, z.array(idSchema)).optional(),
  servicePriceLocks: z.record(idSchema, lockedServicePriceSchema).optional(),
  membershipPurchaseId: idSchema.optional(),
  prepaidByMembership: z.boolean().optional(),
  billingMode: z.enum(['hourly', 'entrance-package']).optional(),
  entranceLimit: z.number().int().positive().max(500).optional(),
  entrancesUsed: z.number().int().nonnegative().optional(),
  notes: z.string().max(2000).optional(),
  status: z.enum(['reserved', 'checked-in', 'checked-out', 'cancelled']).optional(),
  checkedInAt: isoDateTimeSchema.optional(),
  checkedOutAt: isoDateTimeSchema.optional(),
  checkInDocuments: z.array(checkInEvidenceSchema).optional(),
});

export const daycareSchema = daycareCreateSchema.extend({
  id: idSchema,
  status: z.enum(['reserved', 'checked-in', 'checked-out', 'cancelled']),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  checkedInAt: isoDateTimeSchema.optional(),
  checkedOutAt: isoDateTimeSchema.optional(),
  checkInDocuments: z.array(checkInEvidenceSchema).default([]),
});

export const daycarePhotoCreateSchema = z.object({
  fileId: idSchema,
  objectKey: z.string().min(1).max(600),
  fileName: z.string().min(1).max(180),
  contentType: z.string().min(3).max(120),
});

export const daycarePhotoSchema = daycarePhotoCreateSchema.extend({
  id: idSchema,
  daycareId: idSchema,
  createdAt: isoDateTimeSchema,
  createdByUserId: idSchema.optional(),
});

export const stayDogCareSchema = z.object({
  feedingTimes: z.array(z.enum(['morning', 'afternoon', 'night'])).default([]),
  portionCups: z.enum(['0.5', '1', '1.5', '2', '2.5', '3', '4', '5', '6', '7', '8']).optional(),
  feedingComments: z.string().max(2000).optional(),
  belongings: z.string().max(2000).optional(),
  emergencyContact: z.string().max(300).optional(),
  sterilized: z.boolean().default(false),
  allergies: z.string().max(2000).optional(),
  inHeat: z.boolean().default(false),
  lastHeat: z.string().max(300).optional(),
  medication: z.string().max(2000).optional(),
});

export const stayCreateSchema = z.object({
  customerId: idSchema,
  dogId: idSchema,
  dogIds: z.array(idSchema).optional(),
  checkInDate: isoDateSchema,
  checkOutDate: isoDateSchema,
  serviceIds: z.array(idSchema).default([]),
  dogServiceIds: z.record(idSchema, z.array(idSchema)).optional(),
  dogCare: z.record(idSchema, stayDogCareSchema).optional(),
  servicePriceLocks: z.record(idSchema, lockedServicePriceSchema).optional(),
  kennel: z.string().max(80).optional(),
  room: z.string().max(80).optional(),
  assignedStaffUserId: idSchema.optional(),
  notes: z.string().max(2000).optional(),
});

export const stayPhotoCreateSchema = z.object({
  fileId: idSchema,
  objectKey: z.string().min(1).max(600),
  fileName: z.string().min(1).max(180),
  contentType: z.string().min(3).max(120),
});

export const stayUpdateSchema = z.object({
  dogIds: z.array(idSchema).optional(),
  checkInDate: isoDateSchema.optional(),
  checkOutDate: isoDateSchema.optional(),
  serviceIds: z.array(idSchema).optional(),
  dogServiceIds: z.record(idSchema, z.array(idSchema)).optional(),
  dogCare: z.record(idSchema, stayDogCareSchema).optional(),
  servicePriceLocks: z.record(idSchema, lockedServicePriceSchema).optional(),
  kennel: z.string().max(80).optional(),
  room: z.string().max(80).optional(),
  assignedStaffUserId: idSchema.optional(),
  notes: z.string().max(2000).optional(),
  status: z.enum(['reserved', 'checked-in', 'checked-out', 'cancelled']).optional(),
  checkedInAt: isoDateTimeSchema.optional(),
  checkedOutAt: isoDateTimeSchema.optional(),
  checkInDocuments: z.array(checkInEvidenceSchema).optional(),
});

export const staySchema = stayCreateSchema.extend({
  id: idSchema,
  shortId: z.string().min(4).max(12),
  linkedAppointmentIds: z.array(idSchema).optional(),
  status: z.enum(['reserved', 'checked-in', 'checked-out', 'cancelled']),
  checkedInAt: isoDateTimeSchema.optional(),
  checkedOutAt: isoDateTimeSchema.optional(),
  checkInDocuments: z.array(checkInEvidenceSchema).default([]),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const stayLogCreateSchema = z.object({
  type: z.enum(['note', 'feeding', 'medication', 'incident', 'photo']),
  body: z.string().min(1).max(3000),
  occurredAt: isoDateTimeSchema.optional(),
  fileId: idSchema.optional(),
});

export const stayLogSchema = stayLogCreateSchema.extend({
  id: idSchema,
  stayId: idSchema,
  createdAt: isoDateTimeSchema,
  createdByUserId: idSchema.optional(),
});

export const stayPhotoSchema = stayPhotoCreateSchema.extend({
  id: idSchema,
  stayId: idSchema,
  createdAt: isoDateTimeSchema,
  createdByUserId: idSchema.optional(),
});

export const publicStayPhotoSchema = z.object({
  id: idSchema,
  fileName: z.string(),
  contentType: z.string(),
  downloadUrl: z.string().url(),
  createdAt: isoDateTimeSchema,
});

export const publicStayGallerySchema = z.object({
  shortId: z.string(),
  stayId: idSchema,
  photos: z.array(publicStayPhotoSchema),
});

export const publicDaycareGallerySchema = z.object({
  daycareId: idSchema,
  photos: z.array(publicStayPhotoSchema),
});

export const paymentLineItemSchema = z.object({
  description: z.string().max(1000).optional(),
  label: z.string().min(1).max(160),
  quantity: z.number().positive(),
  amountCents: z.number().int().nonnegative(),
  sourceType: z.enum(['daycare', 'stay', 'service', 'membership', 'other']).optional(),
  revenueType: z.enum(['daycare', 'stay', 'service', 'membership', 'other']).optional(),
  sourceId: idSchema.optional(),
  appointmentId: idSchema.optional(),
  serviceId: idSchema.optional(),
  relatedStayId: idSchema.optional(),
  relatedDaycareId: idSchema.optional(),
  billReference: z.string().min(4).max(24).optional(),
  billTotalCents: z.number().int().nonnegative().optional(),
}).superRefine((item, context) => {
  if (item.sourceType === 'other' && (!Number.isSafeInteger(item.quantity) || item.quantity <= 0 || item.amountCents <= 0 || !Number.isSafeInteger(item.amountCents * item.quantity))) {
    context.addIssue({ code: 'custom', path: ['quantity'], message: 'Otro requiere un precio positivo y una cantidad entera positiva.' });
  }
});

export const paymentCreateSchema = z.object({
  customerId: idSchema,
  sourceType: z.enum(['daycare', 'stay', 'service', 'membership', 'other']).optional(),
  sourceId: idSchema.optional(),
  billReference: z.string().min(4).max(24).optional(),
  billTotalCents: z.number().int().nonnegative().optional(),
  items: z.array(paymentLineItemSchema).min(1),
  method: z.enum(['cash', 'card', 'transfer', 'other']).default('cash'),
  notes: z.string().max(1000).optional(),
});

export const paymentUpdateSchema = paymentCreateSchema.partial().extend({
  paidAt: isoDateTimeSchema.optional(),
});

export const paymentProtectedUpdateSchema = z.object({
  masterCode: z.string().min(1).max(128),
  payment: paymentUpdateSchema,
});

export const paymentProtectedDeleteSchema = z.object({
  masterCode: z.string().min(1).max(128),
});

export const masterCodeUpdateSchema = z.object({
  masterCode: z.string().min(4).max(128).nullable(),
});

export const masterCodeStatusSchema = z.object({ configured: z.boolean() });

export const paymentSchema = paymentCreateSchema.extend({
  id: idSchema,
  totalCents: z.number().int().nonnegative(),
  paidAt: isoDateTimeSchema,
  createdByUserId: idSchema.optional(),
});

export const membershipPackageCreateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
  dayCount: z.number().int().positive().max(1000),
  expirationDays: z.number().int().positive().max(3650),
  priceCents: z.number().int().nonnegative(),
  active: z.boolean().default(true),
});

export const membershipPackageUpdateSchema = membershipPackageCreateSchema.partial();

export const membershipPackageSchema = membershipPackageCreateSchema.extend({
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const membershipPurchaseAllocationSchema = z.object({
  dogId: idSchema,
  days: z.number().int().nonnegative().max(1000),
  remainingDays: z.number().int().nonnegative().max(1000),
});

export const membershipPurchaseCreateSchema = z.object({
  packageId: idSchema,
  customerId: idSchema,
  dogAllocations: z.array(membershipPurchaseAllocationSchema.omit({ remainingDays: true }).extend({ remainingDays: z.number().int().nonnegative().max(1000).optional() })).min(1),
  startsAt: isoDateSchema.optional(),
});

export const membershipPurchaseUpdateSchema = z.object({
  dogAllocations: z.array(membershipPurchaseAllocationSchema).min(1).optional(),
  startsAt: isoDateSchema.optional(),
  expiresAt: isoDateSchema.optional(),
  status: z.enum(['active', 'expired', 'depleted']).optional(),
});

export const membershipPurchaseSchema = z.object({
  id: idSchema,
  packageId: idSchema,
  packageName: z.string().min(1).max(120),
  priceCents: z.number().int().nonnegative(),
  customerId: idSchema,
  dogAllocations: z.array(membershipPurchaseAllocationSchema),
  startsAt: isoDateSchema,
  expiresAt: isoDateSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  status: z.enum(['active', 'expired', 'depleted']).default('active'),
});

export const membershipEligibilityQuerySchema = z.object({
  customerId: idSchema,
  dogId: idSchema,
  date: isoDateSchema,
  preferredPurchaseId: idSchema.optional(),
});

export const membershipEligibilitySchema = z.object({
  eligible: z.object({
    membershipPurchaseId: idSchema,
    packageName: z.string(),
    remainingDays: z.number().int().positive(),
    expiresAt: isoDateSchema,
  }).nullable(),
  reason: z.enum(['eligible', 'expired', 'future', 'depleted', 'unpaid', 'inactive', 'missing']).default('missing'),
  message: z.string(),
});

export const fileUploadRequestSchema = z.object({
  fileName: z.string().min(1).max(180),
  contentType: z.string().min(3).max(120),
  sizeBytes: z.number().int().positive().max(10 * 1024 * 1024),
  purpose: z.enum(['dog-photo', 'vaccination-card', 'stay-photo', 'daycare-photo', 'document']),
  ownerId: idSchema.optional(),
});

export const fileUploadResponseSchema = z.object({
  fileId: idSchema,
  uploadUrl: z.string().url(),
  objectKey: z.string(),
});

export const fileDownloadQuerySchema = z.object({
  objectKey: z.string().min(1).max(600),
});

export const fileDownloadResponseSchema = z.object({
  downloadUrl: z.string().url(),
});

export const staffRoleSchema = z.string().trim().min(1).max(80).regex(/^[\w+=,.@-]+$/);

export const staffUserSchema = z.object({
  username: z.string().min(1),
  email: z.string().email().optional(),
  name: z.string().min(1).max(160).optional(),
  givenName: z.string().min(1).max(80).optional(),
  familyName: z.string().min(1).max(80).optional(),
  enabled: z.boolean(),
  status: z.string(),
  roles: z.array(staffRoleSchema),
  createdAt: isoDateTimeSchema.optional(),
  updatedAt: isoDateTimeSchema.optional(),
});

export const staffUserCreateSchema = z.object({
  email: z.string().email(),
  givenName: z.string().min(1).max(80),
  familyName: z.string().min(1).max(80),
  role: staffRoleSchema,
});

export const staffUserRoleUpdateSchema = z.object({
  role: staffRoleSchema,
});

export const staffUserNameUpdateSchema = z.object({
  givenName: z.string().min(1).max(80),
  familyName: z.string().min(1).max(80),
});

export const staffRoleCreateSchema = z.object({
  role: staffRoleSchema,
});

export const moduleIdSchema = z.enum(['dashboard', 'calendar', 'grooming', 'customers', 'client-dogs', 'dogs', 'stays', 'daycares', 'memberships', 'services', 'prices', 'payments', 'users']);

export const roleModulePermissionsSchema = z.object({
  permissions: z.partialRecord(moduleIdSchema, z.array(staffRoleSchema)),
});

export const dashboardSummarySchema = z.object({
  dogsCurrentlyStaying: z.number().int().nonnegative(),
  todaysCheckIns: z.number().int().nonnegative(),
  todaysCheckOuts: z.number().int().nonnegative(),
  occupancy: z.number().nonnegative(),
  pendingGrooming: z.number().int().nonnegative(),
  revenueTodayCents: z.number().int().nonnegative(),
  revenueMonthCents: z.number().int().nonnegative(),
  paymentCountToday: z.number().int().nonnegative().default(0),
  periodLabel: z.string().optional(),
  periodRevenueCents: z.number().int().nonnegative().default(0),
  periodPaymentCount: z.number().int().nonnegative().default(0),
  revenueByType: z.array(z.object({
    type: z.enum(['stay', 'daycare', 'service', 'membership', 'other']),
    amountCents: z.number().int().nonnegative(),
  })).default([]),
  revenueByServiceCategory: z.array(z.object({
    category: z.enum(['daycare', 'boarding', 'grooming', 'spa', 'transportation', 'medical', 'treats', 'fee', 'other']),
    amountCents: z.number().int().nonnegative(),
  })).default([]),
  chart: z.array(z.object({ label: z.string(), value: z.number().nonnegative(), amountCents: z.number().int().nonnegative().optional() })).default([]),
  recentActivity: z.array(z.object({
    id: idSchema,
    type: z.enum(['payment', 'grooming', 'check-in', 'check-out', 'photo', 'other']).default('other'),
    message: z.string(),
    actorName: z.string().optional(),
    createdAt: isoDateTimeSchema,
  })),
  alerts: z.array(z.object({
    id: idSchema,
    type: z.enum(['grooming', 'spa', 'transportation', 'stay-check-in', 'stay-check-out', 'daycare-check-in', 'daycare-check-out', 'other']).default('other'),
    level: z.enum(['info', 'warning', 'critical']),
    message: z.string(),
    scheduledAt: isoDateTimeSchema,
  })),
});

export const dashboardQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'year', 'custom']).default('today'),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});

export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().optional(),
  search: z.string().trim().min(1).max(120).optional(),
  q: z.string().trim().min(1).max(120).optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  status: z.string().trim().min(1).max(80).optional(),
  category: z.string().trim().min(1).max(80).optional(),
  customerId: idSchema.optional(),
  ownerId: idSchema.optional(),
});

export const customerSearchQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().min(2).max(80).optional(),
});

export type Customer = z.infer<typeof customerSchema>;
export type CustomerCreate = z.infer<typeof customerCreateSchema>;
export type CustomerUpdate = z.infer<typeof customerUpdateSchema>;
export type Dog = z.infer<typeof dogSchema>;
export type DogCreate = z.infer<typeof dogCreateSchema>;
export type DogUpdate = z.infer<typeof dogUpdateSchema>;
export type Service = z.infer<typeof serviceSchema>;
export type ServiceCreate = z.infer<typeof serviceCreateSchema>;
export type ServiceUpdate = z.infer<typeof serviceUpdateSchema>;
export type Appointment = z.infer<typeof appointmentSchema>;
export type AppointmentCreate = z.infer<typeof appointmentCreateSchema>;
export type AppointmentUpdate = z.infer<typeof appointmentUpdateSchema>;
export type Price = z.infer<typeof priceSchema>;
export type PriceCreate = z.infer<typeof priceCreateSchema>;
export type Daycare = z.infer<typeof daycareSchema>;
export type DaycareCreate = z.infer<typeof daycareCreateSchema>;
export type DaycareUpdate = z.infer<typeof daycareUpdateSchema>;
export type DaycarePhoto = z.infer<typeof daycarePhotoSchema>;
export type DaycarePhotoCreate = z.infer<typeof daycarePhotoCreateSchema>;
export type CheckInEvidence = z.infer<typeof checkInEvidenceSchema>;
export type CheckInRequest = z.infer<typeof checkInRequestSchema>;
export type Stay = z.infer<typeof staySchema>;
export type StayCreate = z.infer<typeof stayCreateSchema>;
export type StayUpdate = z.infer<typeof stayUpdateSchema>;
export type StayDogCare = z.infer<typeof stayDogCareSchema>;
export type StayLog = z.infer<typeof stayLogSchema>;
export type StayLogCreate = z.infer<typeof stayLogCreateSchema>;
export type StayPhoto = z.infer<typeof stayPhotoSchema>;
export type StayPhotoCreate = z.infer<typeof stayPhotoCreateSchema>;
export type PublicStayGallery = z.infer<typeof publicStayGallerySchema>;
export type PublicDaycareGallery = z.infer<typeof publicDaycareGallerySchema>;
export type PublicStayPhoto = z.infer<typeof publicStayPhotoSchema>;
export type Payment = z.infer<typeof paymentSchema>;
export type PaymentCreate = z.infer<typeof paymentCreateSchema>;
export type PaymentUpdate = z.infer<typeof paymentUpdateSchema>;
export type PaymentLineItem = z.infer<typeof paymentLineItemSchema>;
export type MembershipPackage = z.infer<typeof membershipPackageSchema>;
export type MembershipPackageCreate = z.infer<typeof membershipPackageCreateSchema>;
export type MembershipPackageUpdate = z.infer<typeof membershipPackageUpdateSchema>;
export type MembershipPurchase = z.infer<typeof membershipPurchaseSchema>;
export type MembershipPurchaseCreate = z.infer<typeof membershipPurchaseCreateSchema>;
export type MembershipPurchaseUpdate = z.infer<typeof membershipPurchaseUpdateSchema>;
export type MembershipEligibility = z.infer<typeof membershipEligibilitySchema>;
export type FileUploadRequest = z.infer<typeof fileUploadRequestSchema>;
export type FileUploadResponse = z.infer<typeof fileUploadResponseSchema>;
export type FileDownloadResponse = z.infer<typeof fileDownloadResponseSchema>;
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;
export type StaffRole = z.infer<typeof staffRoleSchema>;
export type StaffUser = z.infer<typeof staffUserSchema>;
export type StaffUserCreate = z.infer<typeof staffUserCreateSchema>;
export type StaffUserRoleUpdate = z.infer<typeof staffUserRoleUpdateSchema>;
export type StaffUserNameUpdate = z.infer<typeof staffUserNameUpdateSchema>;
export type StaffRoleCreate = z.infer<typeof staffRoleCreateSchema>;
export type ModuleId = z.infer<typeof moduleIdSchema>;
export type RoleModulePermissions = z.infer<typeof roleModulePermissionsSchema>;
